import { useCallback, useRef, useState } from 'react';
import { Upload, Loader2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';

const IK_URL = 'https://upload.imagekit.io/api/v1/files/upload';

/**
 * Uploads a single File to ImageKit using auth params from `authFn`.
 * authFn(folder) -> { publicKey, signature, token, expire, folder, urlEndpoint }
 * Defaults to the admin auth endpoint; pass a public auth fn for unauthenticated forms.
 */
async function uploadToImageKit(file, folder, onProgress, authFn) {
  const auth = await (authFn ? authFn(folder) : adminApi.imagekitAuth(folder));
  const form = new FormData();
  form.append('file', file);
  // Sanitize filename: ImageKit accepts most chars but strip weird ones for safety
  const safeName = (file.name || `upload-${Date.now()}`).replace(/[^\w.\-]+/g, '_');
  form.append('fileName', safeName);
  form.append('publicKey', auth.publicKey);
  form.append('signature', auth.signature);
  form.append('token', auth.token);
  form.append('expire', String(auth.expire));
  form.append('folder', auth.folder);
  form.append('useUniqueFileName', 'true');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', IK_URL, true);
    // Generous timeout for slow mobile connections + large videos
    xhr.timeout = 5 * 60 * 1000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onload = () => {
      let res = {};
      try { res = JSON.parse(xhr.responseText || '{}'); } catch { /* keep res = {} */ }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(res?.message || `Upload failed (HTTP ${xhr.status})`));
        return;
      }

      // ImageKit normally returns `url`. If absent for any reason, reconstruct it
      // from urlEndpoint + filePath so the file (which IS uploaded) is still usable.
      let finalUrl = res.url;
      if (!finalUrl && res.filePath && auth.urlEndpoint) {
        finalUrl = `${auth.urlEndpoint.replace(/\/$/, '')}${res.filePath.startsWith('/') ? '' : '/'}${res.filePath}`;
      }
      if (!finalUrl) {
        // Truly unparseable — surface for debugging but don't lose the upload
        console.warn('ImageKit upload OK but no URL in response:', res);
        reject(new Error('Upload succeeded on ImageKit but URL was not returned. Check ImageKit portal.'));
        return;
      }
      resolve({ ...res, url: finalUrl });
    };
    xhr.send(form);
  });
}

/**
 * Drag-and-drop uploader that pushes one or many files to ImageKit
 * and calls onUploaded(urls[]) for the parent to persist.
 *
 * Props:
 *   accept       - 'image' | 'video' | 'image+video'
 *   multiple     - allow multi-file selection
 *   folder       - subfolder inside ImageKit (e.g. 'findyourmua/artists/<slug>')
 *   onUploaded   - (urls: string[]) => void
 *   label        - small label above the dropzone
 *   compact      - shrink for use inline in lists
 */
export default function ImageKitUploader({
  accept = 'image',
  multiple = false,
  folder = 'findyourmua',
  onUploaded,
  label = 'Upload',
  compact = false,
  testId,
  authFn = null,
  maxBytes = 25 * 1024 * 1024,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [items, setItems] = useState([]); // { name, progress, status }

  const acceptAttr = accept === 'image' ? 'image/*'
    : accept === 'video' ? 'video/*'
    : accept === 'document' ? 'image/*,application/pdf'
    : 'image/*,video/*';

  const processFiles = useCallback(async (files) => {
    const list = Array.from(files).filter(f => {
      if (f.size > maxBytes) { toast.error(`${f.name}: exceeds ${Math.round(maxBytes / 1024 / 1024)}MB limit`); return false; }
      return true;
    });
    if (!list.length) return;
    const batch = list.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name,
      file: f,
      progress: 0,
      status: 'uploading',
    }));
    setItems(prev => [...prev, ...batch.map(({ file, ...rest }) => rest)]);

    const results = await Promise.all(batch.map(async (item) => {
      try {
        const res = await uploadToImageKit(item.file, folder, (p) => {
          setItems(cur => cur.map(it => it.id === item.id ? { ...it, progress: p } : it));
        }, authFn);
        setItems(cur => cur.map(it => it.id === item.id ? { ...it, progress: 100, status: 'done' } : it));
        return res.url;
      } catch (err) {
        console.error('ImageKit upload failed:', item.name, err);
        toast.error(`${item.name}: ${err.message || 'upload failed'}`);
        setItems(cur => cur.map(it => it.id === item.id ? { ...it, status: 'error' } : it));
        return null;
      }
    }));
    const urls = results.filter(Boolean);
    if (urls.length) {
      onUploaded(urls);
      setTimeout(() => setItems(cur => cur.filter(it => it.status !== 'done')), 2500);
    }
  }, [folder, onUploaded, authFn, maxBytes]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFiles(e.dataTransfer.files);
  };
  const onFile = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const sizeClass = compact ? 'p-3' : 'p-6';

  return (
    <div data-testid={testId}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`border border-dashed cursor-pointer transition-colors text-center ${sizeClass} ${
          dragging ? 'border-[#eb5381] bg-[#fff8ee]' : 'border-neutral-300 hover:border-[#eb5381] hover:bg-[#fafafa]'
        }`}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={acceptAttr}
          multiple={multiple}
          onChange={onFile}
        />
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-neutral-600">
          {compact ? <Plus className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
          <span>{label}</span>
        </div>
        {!compact && (
          <p className="mt-1.5 text-xs text-neutral-500">
            Drag &amp; drop or click to choose — {multiple ? 'multiple files' : 'one file'}{accept === 'video' ? ' · videos only' : accept === 'image' ? ' · images only' : ''}
          </p>
        )}
      </div>

      {items.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-2 text-xs text-neutral-600">
              {it.status === 'uploading' && <Loader2 className="w-3 h-3 animate-spin text-[#eb5381]" />}
              {it.status === 'done' && <span className="text-[#16a34a]">✓</span>}
              {it.status === 'error' && <X className="w-3 h-3 text-red-500" />}
              <span className="truncate flex-1">{it.name}</span>
              {it.status === 'uploading' && <span>{it.progress}%</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
